import { observable, action } from "mobx";

/**
 * Client-side state for the copilot onboarding flows (new-user 10-step setup
 * and existing-user account digest). Owns the step machine, the artifacts
 * collected along the way, resume, and the two-dismissal / completed gates.
 * Persisted per member in localStorage so a reload or a next-login resumes
 * where the user left off and never refires after completion.
 */

const key = memberId => `sg_copilot_onboarding_${memberId || "anon"}`;

const EMPTY_ARTIFACTS = {
  website: "",
  draftProfile: null,
  savedProfileId: "",
  icpFilters: null,
  searchTotal: 0,
  listId: "",
  listName: "",
  enrichChoice: "",
  emailConnected: false,
  linkedInConnected: false,
  sequenceId: ""
};

class OnboardingCopilotStore {
  @observable mode = null; // "onboarding" | "digest" | null
  @observable step = 0;
  @observable artifacts = { ...EMPTY_ARTIFACTS };
  @observable dismissCount = 0;
  status = "idle"; // "idle" | "active" | "done" | "dismissed"
  memberId = "";

  @action begin = (mode, memberId) => {
    this.mode = mode;
    this.memberId = memberId;
    // Only a never-started run gets cleared. Keying on "not active" meant
    // re-entering a completed or dismissed run wiped the record of what it
    // built (saved profile, list, sequence). "Start over" sets idle first, so
    // that path still resets.
    if (this.status === "idle") {
      this.step = 0;
      this.artifacts = { ...EMPTY_ARTIFACTS };
    }
    this.status = "active";
    this.persist();
  };

  @action setStep = n => {
    this.step = n;
    this.persist();
  };

  @action patchArtifacts = obj => {
    this.artifacts = { ...this.artifacts, ...obj };
    this.persist();
  };

  @action dismiss = () => {
    this.dismissCount += 1;
    this.status = this.dismissCount >= 2 ? "dismissed" : "active";
    this.mode = null;
    this.persist();
  };

  @action complete = () => {
    this.status = "done";
    this.mode = null;
    this.persist();
  };

  // "fresh" -> never started, offer from step 0.
  // "resume" -> was active past step 0, offer to pick up.
  // null -> completed or dismissed twice, never offer again.
  shouldOffer = memberId => {
    this.hydrate(memberId);
    if (this.status === "done" || this.status === "dismissed") return null;
    if (this.status === "active" && this.step > 0) return "resume";
    return "fresh";
  };

  persist = () => {
    try {
      localStorage.setItem(
        key(this.memberId),
        JSON.stringify({
          status: this.status,
          step: this.step,
          artifacts: this.artifacts,
          dismissCount: this.dismissCount
        })
      );
    } catch (e) {
      /* storage unavailable — resume degrades to a fresh start */
    }
  };

  @action hydrate = memberId => {
    this.memberId = memberId;
    try {
      const raw = localStorage.getItem(key(memberId));
      if (!raw) return;
      const saved = JSON.parse(raw);
      this.status = saved.status || "idle";
      this.step = saved.step || 0;
      this.artifacts = { ...EMPTY_ARTIFACTS, ...(saved.artifacts || {}) };
      this.dismissCount = saved.dismissCount || 0;
    } catch (e) {
      /* corrupt or unavailable — treat as fresh */
    }
  };
}

const onboardingCopilotStore = new OnboardingCopilotStore();
export default onboardingCopilotStore;
export { OnboardingCopilotStore, EMPTY_ARTIFACTS };
